'use client'

import Image from 'next/image'
import { getFileFromNwCDN, Section } from '@nwwp/acquisition-ui'
import { PageViewed } from '@nwwp/segment-analytics'
import { cn } from '@nwwp/ui-shadcn'
import { EQUITY_WEBINAR_SLUG, type WebinarLeadMagnetSlug } from '~/app/equity-webinar/constants'
import { WebinarRegistrationForm } from '~/components/WebinarRegistrationForm'

const COVERAGE = [
  'Equity mechanics: vesting, taxes, and withholding',
  'Concentration risk and when to diversify',
  'Tax timing: 83(b) elections, AMT, dispositions',
  'How equity fits your bigger financial picture',
  'Live Q&A with two fiduciary advisors',
]

/**
 * Webinar-specific headshots, not the PARTNERS cut-outs — those are masked for the
 * "meet your partners" treatment and crop badly here. `objectPosition` keeps each
 * face in frame at the design's 280px cover height.
 */
const SPEAKERS = [
  {
    name: 'Ryan Sterling, CFA®',
    role: 'Founder & CEO',
    bio: 'Ryan has more than 20 years of experience in wealth management at top firms including Capital Group Private Client Service, Goldman Sachs, and Bernstein Global Wealth Management. Ryan holds an MBA in Investment Management from Vanderbilt University.',
    photo: getFileFromNwCDN('ryan-sterling-webinar@2x-20260902.webp'),
    objectPosition: 'center 8%',
  },
  {
    name: 'Peter McGinley, CFP®',
    role: 'Financial Advisor',
    bio: 'Peter is a Certified Financial Planner® who has spent 10 years in wealth management. A graduate of Providence College, he previously worked at BNY before joining NerdWallet Wealth Partners.',
    photo: getFileFromNwCDN('peter-mcginley-webinar@2x-20260902.webp'),
    /*
     * Tuned so Peter's eyes land on Ryan's eye line (140px) in the real 560x280 crop.
     * The two photos are framed differently — Ryan's head fills ~190px of the box,
     * Peter's ~130px — so their hairlines cannot also match; eye line is what reads as
     * "even" across a row. Re-tune both values together if either photo is replaced.
     */
    objectPosition: 'center 11%',
  },
]

export function EquityWebinarContent({
  eventLine,
  leadMagnetSlug = EQUITY_WEBINAR_SLUG,
  pageName = 'Employee Equity Webinar Landing Page',
}: {
  eventLine: string
  leadMagnetSlug?: WebinarLeadMagnetSlug
  pageName?: string
}) {
  return (
    <PageViewed pageName={pageName}>
      <main className="flex-1 bg-background">
        <div className="dark rounded-b-4xl bg-black">
          <Section maxWidth="full" className="text-foreground">
            <div className="flex flex-col gap-12 tablet:flex-row tablet:items-start tablet:gap-16">
              <div className="flex flex-1 flex-col gap-6">
                <p className="text-subtitle font-bold text-foreground uppercase">Live webinar</p>
                <h1 className="text-title-2xl text-foreground">
                  Your equity compensation <em>deserves a plan.</em>
                </h1>
                <p className="text-body text-foreground">
                  {eventLine}: a free, live session on RSUs, stock options and ESPPs.
                </p>

                <div className="flex flex-col gap-4">
                  <h2 className="text-subtitle font-bold text-green-lighter uppercase">What we&rsquo;ll cover</h2>
                  <ul className="flex flex-col gap-3">
                    {COVERAGE.map((item) => (
                      <li key={item} className="flex gap-3 text-body text-foreground">
                        <span aria-hidden="true" className="text-green-lighter">
                          +
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/*
                `tablet:flex-1` against the left column's `flex-1` splits the hero 50/50
                from 769px up — both columns get `flex: 1 1 0%`, so neither's content
                width influences the split. Uncapped on purpose: the rest of the page is
                full-bleed within Section's px-25, so the hero tracks it. Below 769px the
                columns stack and this card goes full width.
              */}
              <div className="flex w-full flex-col gap-6 self-start tablet:flex-1">
                {/*
                  `light` re-establishes the light token context inside the dark island,
                  and text-foreground re-resolves the colour against it — setting the
                  variable alone leaves the white colour inherited from the ancestor.
                */}
                <div
                  id="signup"
                  className={cn(
                    'light rounded-3xl bg-card text-foreground',
                    'p-[clamp(22px,4vw,30px)] shadow-[0_8px_28px_rgba(0,0,0,0.45)]'
                  )}
                >
                  <h2 className="text-title-sm mb-6 text-foreground">Save your seat</h2>
                  <WebinarRegistrationForm leadMagnetSlug={leadMagnetSlug} />
                </div>
              </div>
            </div>
          </Section>
        </div>

        <Section>
          <div className="flex flex-col items-center gap-4 text-center">
            <h2 className="text-title-xl text-foreground">Meet your speakers.</h2>
            <p className="max-w-2xl text-body text-foreground">
              Ryan and Peter are fee-only financial advisors at NerdWallet Wealth Partners. They help clients make sense
              of their equity, including vesting, taxes, and concentration, and the trade-offs that affect goals like
              retiring early or buying a home.
            </p>
          </div>

          <div className="mt-12 grid gap-6 tablet:grid-cols-2">
            {SPEAKERS.map((speaker) => (
              <article key={speaker.name} className="flex flex-col gap-4 rounded-4xl bg-card p-6">
                <div className="relative h-70 w-full overflow-hidden rounded-2xl">
                  <Image
                    src={speaker.photo}
                    alt={speaker.name}
                    fill
                    sizes="(min-width: 769px) 500px, 100vw"
                    style={{ objectPosition: speaker.objectPosition }}
                    className="object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-body-bold-lg text-foreground">{speaker.name}</h3>
                  <p className="text-body-sm text-neutral-darker">{speaker.role}</p>
                </div>
                <p className="text-body-sm text-foreground">{speaker.bio}</p>
              </article>
            ))}
          </div>
        </Section>
      </main>
    </PageViewed>
  )
}
